import { useRef, useState } from 'react';
import { ChevronDown, Lock } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { useTranslations } from 'use-intl';
import { ServiceRegistry } from '@/services/ServiceRegistry';

interface MappingCandidate {
  number: string;
  ledgerEntryCount: number;
  firstEntryDate: string | null;
}

interface MappingCellAccount {
  id: string;
  bankName: string;
  chartAccountNumber?: string;
  chartAccountName?: string;
  chartMappingLocked?: boolean;
  chartMappingLockedEntryCount?: number;
  chartMappingLockedFirstEntryDate?: string;
  chartMappingCandidates?: MappingCandidate[];
  chartMappingSwapOnly?: boolean;
}

interface BankAccountChartMappingCellProps {
  account: MappingCellAccount;
  accounts: MappingCellAccount[];
  onChanged: () => void | Promise<void>;
  /** FIN-1021/AC14: entries currently waiting on this account's mapping. Picking a number
   * books and locks them within about a minute (S5) -- shown as one inline warning line in
   * this menu, never a modal. */
  waitingCount?: number;
}

type MappingOption =
  | { kind: 'change'; number: string }
  | { kind: 'adopt'; number: string; ledgerEntryCount: number; firstEntryDate: string | null }
  | { kind: 'swap'; targetId: string; targetBankName: string; number: string; disabledReason?: string };

/** `number` is the target's number as shown; the swap sends it and this row's own as the expected pair. */
type PendingSwap = { targetId: string; targetBankName: string; number: string };
type PendingAdoption = { number: string; ledgerEntryCount: number; firstEntryDate: string | null };

type Translator = ReturnType<typeof useTranslations<'consultantBank.mapping'>>;

function errorCode(error: unknown): string | undefined {
  return (error as { error?: string })?.error;
}

function NumberAndName({ account }: { account: MappingCellAccount }) {
  return (
    <span className="whitespace-nowrap">
      <span className="font-mono">{account.chartAccountNumber}</span>
      {account.chartAccountName && <span className="text-muted-foreground"> {account.chartAccountName}</span>}
    </span>
  );
}

/** F5/AC14: the padlock replaces the change control entirely once any journal entry exists. */
function LockedMappingBadge({ account }: { account: MappingCellAccount }) {
  const t = useTranslations('consultantBank.mapping');
  const count = account.chartMappingLockedEntryCount ?? 0;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex items-center gap-1.5 cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <NumberAndName account={account} />
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {t('lockedTooltip', {
            count,
            hasSince: account.chartMappingLockedFirstEntryDate ? 'yes' : 'no',
            date: account.chartMappingLockedFirstEntryDate ?? '',
          })}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function lockedSiblingReason(sibling: MappingCellAccount, t: Translator): string | undefined {
  if (!sibling.chartMappingLocked) return undefined;
  return t('lockedSibling', { bank: sibling.bankName, count: sibling.chartMappingLockedEntryCount ?? 0 });
}

/**
 * The numbers this account may move to. The server sends them (FIN-968: the country module owns
 * the range, and each carries its ledger history); an older response without them falls back to
 * the current number's own ten-block, with the server's CHART_ACCOUNT_HAS_HISTORY refusal still
 * catching a ledger-occupied pick.
 */
function candidatesFor(account: MappingCellAccount): MappingCandidate[] {
  if (account.chartMappingCandidates) return account.chartMappingCandidates;
  if (!account.chartAccountNumber) return [];
  const base = Number(account.chartAccountNumber.slice(0, 3) + '0');
  return Array.from({ length: 10 }, (_, i) => String(base + i).padStart(4, '0'))
    .filter((number) => number !== account.chartAccountNumber)
    .map((number) => ({ number, ledgerEntryCount: 0, firstEntryDate: null }));
}

/**
 * F6: a free number becomes a plain change (AC13); a number with ledger history becomes a
 * "Continue ledger" option behind a confirmation (FIN-968 G1); a number a sibling holds becomes
 * "Swap with" (AC23), disabled with a named reason when that sibling is itself locked (R-SWAP-2).
 * An unmapped account has nothing to swap, and the primary account can only swap (G8).
 */
function buildMappingOptions(account: MappingCellAccount, accounts: MappingCellAccount[], t: Translator): MappingOption[] {
  const siblingByNumber = new Map(
    accounts
      .filter((a) => a.id !== account.id && a.chartAccountNumber)
      .map((a) => [a.chartAccountNumber as string, a]),
  );
  const isMapped = !!account.chartAccountNumber;

  const options: MappingOption[] = [];
  for (const candidate of candidatesFor(account)) {
    const sibling = siblingByNumber.get(candidate.number);
    if (sibling) {
      if (isMapped) {
        options.push({ kind: 'swap', targetId: sibling.id, targetBankName: sibling.bankName, number: candidate.number, disabledReason: lockedSiblingReason(sibling, t) });
      }
    } else if (account.chartMappingSwapOnly) {
      continue;
    } else if (candidate.ledgerEntryCount > 0) {
      options.push({ kind: 'adopt', ...candidate });
    } else {
      options.push({ kind: 'change', number: candidate.number });
    }
  }
  return options;
}

function rangeLabel(account: MappingCellAccount, t: Translator): string {
  const numbers = candidatesFor(account).map((c) => c.number);
  return numbers.length ? `${numbers[0]}–${numbers[numbers.length - 1]}` : t('thisRange');
}

/**
 * Multi Bank Account (FIN-90) PR4: the per-row F4/F5 control -- an inline menu at zero journal
 * entries (AC13), a padlock + tooltip once any exist (AC14), and a "Swap with" action for any
 * number a sibling account already holds (AC23), applied under one confirmation dialog since a
 * swap also touches a second account's mapping (R-SWAP-1). FIN-968: an unmapped account gets a
 * "Map to…" menu (G6), and continuing an existing ledger needs its own confirmation (G1).
 *
 * Renders a malformed mapping as "Not mapped" rather than throwing: App.tsx has exactly one
 * root Sentry.ErrorBoundary around all of <Routes>, so one bad row would otherwise blank the
 * entire consultant app.
 */
export default function BankAccountChartMappingCell({ account, accounts, onChanged, waitingCount = 0 }: BankAccountChartMappingCellProps) {
  const t = useTranslations('consultantBank.mapping');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // A double-click lands its second click on the closing menu's item, which the disabled
  // trigger does not cover; a ref, because both clicks can arrive before a re-render.
  const changeInFlight = useRef(false);
  const [pendingSwap, setPendingSwap] = useState<PendingSwap | null>(null);
  const [pendingAdoption, setPendingAdoption] = useState<PendingAdoption | null>(null);

  if (!account?.id) {
    return <span className="text-muted-foreground">{t('notMapped')}</span>;
  }
  if (account.chartMappingLocked) {
    return <LockedMappingBadge account={account} />;
  }

  const isMapped = !!account.chartAccountNumber;
  const options = buildMappingOptions(account, accounts, t);
  if (!isMapped && !account.chartMappingCandidates) {
    return <span className="text-muted-foreground">{t('notMapped')}</span>;
  }

  const handleChange = async (newAccountNumber: string, adoptLedgerHistory = false) => {
    if (changeInFlight.current) return;
    changeInFlight.current = true;
    setIsSubmitting(true);
    try {
      await ServiceRegistry.getAccountService().changeChartMapping(account.id, newAccountNumber, adoptLedgerHistory);
      toast.success(t(adoptLedgerHistory ? 'toasts.continuing' : 'toasts.changed', { number: newAccountNumber }));
      await onChanged();
    } catch (error) {
      // The server is authoritative -- the ledger or a sibling can change between this row
      // rendering and the click landing.
      const code = errorCode(error);
      if (code === 'CHART_ACCOUNT_HAS_HISTORY') {
        const details = (error as { details?: { journalEntryItemCount?: number; firstEntryDate?: string | null } }).details;
        setPendingAdoption({
          number: newAccountNumber,
          ledgerEntryCount: details?.journalEntryItemCount ?? 0,
          firstEntryDate: details?.firstEntryDate ?? null,
        });
      } else if (code === 'CHART_MAPPING_LOCKED') {
        toast.error(t('toasts.justLocked'));
        await onChanged();
      } else if (code === 'CHART_ACCOUNT_UNAVAILABLE') {
        toast.error(t('toasts.taken'));
        await onChanged();
      } else if (code === 'CHART_ACCOUNT_OUT_OF_RANGE') {
        toast.error(t('toasts.outOfRange', { number: newAccountNumber }));
        await onChanged();
      } else if (code === 'CHART_MAPPING_PRIMARY_SWAP_ONLY') {
        toast.error(t('toasts.primarySwapOnly'));
        await onChanged();
      }
      // Anything else was already reported, with the server's own message, by AccountApiClient.
    } finally {
      changeInFlight.current = false;
      setIsSubmitting(false);
    }
  };

  const handleConfirmAdoption = async () => {
    if (!pendingAdoption) return;
    const { number } = pendingAdoption;
    setPendingAdoption(null);
    await handleChange(number, true);
  };

  const handleConfirmSwap = async () => {
    if (!pendingSwap) return;
    const { targetId, number: targetNumber } = pendingSwap;
    const ownNumber = account.chartAccountNumber;
    if (!ownNumber) return;
    setPendingSwap(null);
    setIsSubmitting(true);
    try {
      const result = await ServiceRegistry.getAccountService().swapChartMappings(account.id, targetId, ownNumber, targetNumber);
      if ('lockedAccountId' in result) {
        // R-SWAP-2: server-side re-check found a lock this row's own render didn't know about yet.
        const lockedName = accounts.find((a) => a.id === result.lockedAccountId)?.bankName ?? t('oneAccount');
        toast.error(t('toasts.swapLocked', { name: lockedName, count: result.journalEntryItemCount }));
        await onChanged();
        return;
      }
      toast.success(t('toasts.swapped'));
      await onChanged();
    } catch (error) {
      const code = errorCode(error);
      if (code === 'CHART_MAPPING_CHANGED') {
        toast.error(t('toasts.staleSwap'));
        await onChanged();
      } else if (code === 'CHART_ACCOUNT_UNAVAILABLE') {
        toast.error(t('toasts.swapTaken'));
        await onChanged();
      } else if (code === 'CHART_ACCOUNT_OUT_OF_RANGE') {
        toast.error(t('toasts.swapOutOfRange'));
        await onChanged();
      }
      // Anything else was already reported, with the server's own message, by AccountApiClient.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    // No wrapper-level stopPropagation needed: the trigger button, DropdownMenuContent, and
    // AlertDialogContent each already stop their own click from bubbling to the row.
    <span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={isSubmitting}
            className="inline-flex items-center gap-1 whitespace-nowrap rounded hover:bg-muted disabled:opacity-50"
            onClick={(e) => e.stopPropagation()}
          >
            {isMapped ? <NumberAndName account={account} /> : <span className="text-muted-foreground">{t('notMappedMenu')}</span>}
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent onClick={(e) => e.stopPropagation()}>
          {waitingCount > 0 && (
            <DropdownMenuLabel className="font-normal text-amber-800 whitespace-normal max-w-[220px]">
              {t('waiting', { count: waitingCount })}
            </DropdownMenuLabel>
          )}
          {account.chartMappingSwapOnly && (
            <DropdownMenuLabel className="font-normal text-muted-foreground">
              {t('swapOnly')}
            </DropdownMenuLabel>
          )}
          {options.length === 0 && (
            <DropdownMenuItem disabled>
              {account.chartMappingSwapOnly ? t('noSwapTarget') : t('noFreeNumber', { range: rangeLabel(account, t) })}
            </DropdownMenuItem>
          )}
          {options.map((opt) => {
            if (opt.kind === 'change') {
              return (
                <DropdownMenuItem key={opt.number} onSelect={() => handleChange(opt.number)}>
                  {opt.number}
                </DropdownMenuItem>
              );
            }
            if (opt.kind === 'adopt') {
              return (
                <DropdownMenuItem
                  key={opt.number}
                  onSelect={() => setPendingAdoption({ number: opt.number, ledgerEntryCount: opt.ledgerEntryCount, firstEntryDate: opt.firstEntryDate })}
                >
                  {t('continueLedgerOption', { number: opt.number, count: opt.ledgerEntryCount })}
                </DropdownMenuItem>
              );
            }
            return (
              <DropdownMenuItem
                key={opt.targetId}
                disabled={!!opt.disabledReason}
                title={opt.disabledReason}
                onSelect={() => setPendingSwap({ targetId: opt.targetId, targetBankName: opt.targetBankName, number: opt.number })}
              >
                {t('swapWith', { bank: opt.targetBankName, number: opt.number })}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={pendingSwap !== null} onOpenChange={(open) => !open && setPendingSwap(null)}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('swapTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('swapBody', {
                ownNumber: account.chartAccountNumber ?? '',
                targetBank: pendingSwap?.targetBankName ?? '',
                targetNumber: pendingSwap?.number ?? '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSwap}>{t('swap')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pendingAdoption !== null} onOpenChange={(open) => !open && setPendingAdoption(null)}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('continueTitle', { number: pendingAdoption?.number ?? '' })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('continueBody', {
                number: pendingAdoption?.number ?? '',
                count: pendingAdoption?.ledgerEntryCount ?? 0,
                hasSince: pendingAdoption?.firstEntryDate ? 'yes' : 'no',
                date: pendingAdoption?.firstEntryDate ?? '',
                bank: account.bankName,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmAdoption}>{t('continueLedger')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </span>
  );
}
